import React, { useEffect, useMemo, useState } from "react";
import { users as usersApi } from "../api/endpoints";
import { DataTableShell, EmptyState, MetricStrip, PageHeader, SectionCard, SegmentedViewSwitch } from "../components/Workspace";
import type { User, UserActivitySummary } from "../types";

type ViewKey = "online" | "recent" | "all" | "needs_attention";
const USER_PAGE_SIZE = 5000;

function fullName(user: User) {
  return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;
}

function formatDateTime(value?: string | null) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function daysSince(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((Date.now() - date.getTime()) / 86400000);
}

function isRecent(user: User) {
  if (!user.last_login) return false;
  const days = daysSince(user.last_login);
  return days != null && days <= 7;
}

async function fetchAllUsers() {
  const allUsers: User[] = [];
  let offset = 0;
  let totalCount: number | null = null;

  while (totalCount == null || allUsers.length < totalCount) {
    const params = new URLSearchParams({
      limit: String(USER_PAGE_SIZE),
      offset: String(offset),
      active_only: "false",
    });
    const response = await usersApi.list(params.toString());
    allUsers.push(...response.items);
    totalCount = response.count;

    if (response.items.length === 0) break;
    offset += response.items.length;
  }

  return allUsers;
}

export default function UserActivityPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [summary, setSummary] = useState<UserActivitySummary | null>(null);
  const [view, setView] = useState<ViewKey>("online");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; message: string } | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [userRows, summaryRow] = await Promise.all([
        fetchAllUsers(),
        usersApi.activitySummary(),
      ]);
      setUsers(userRows);
      setSummary(summaryRow);
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Unable to load user activity." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const activeUsers = users.filter((user) => user.is_active);
  const onlineUsers = activeUsers.filter((user) => user.is_logged_in);
  const recentUsers = activeUsers.filter(isRecent);
  const attentionUsers = users.filter((user) =>
    !user.is_active || user.is_blocked || !user.has_usable_password || !user.last_login
  );

  const filteredUsers = useMemo(() => {
    const base =
      view === "online"
        ? onlineUsers
        : view === "recent"
          ? recentUsers
          : view === "needs_attention"
            ? attentionUsers
            : users;
    const needle = search.trim().toLowerCase();
    if (!needle) return base;
    return base.filter((user) =>
      [fullName(user), user.email, user.role?.name || "", user.management_company_name, user.report_management_company_name, user.report_view_mode]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [attentionUsers, onlineUsers, recentUsers, search, users, view]);

  const metricItems = [
    {
      label: "Online Now",
      value: summary?.logged_in_users ?? onlineUsers.length,
      detail: "Based on current login flag",
      tone: "success" as const,
    },
    {
      label: "Active Users",
      value: summary?.total_active_users ?? activeUsers.length,
      detail: "Enabled accounts",
      tone: "brand" as const,
    },
    {
      label: "7 Day Sign Ins",
      value: summary?.signed_in_last_7d ?? recentUsers.length,
      detail: "Last login within 7 days",
      tone: "accent" as const,
    },
    {
      label: "Needs Attention",
      value: attentionUsers.length,
      detail: "Blocked, inactive, no password, or never signed in",
      tone: "warning" as const,
    },
  ];

  const resetLogin = async (user: User) => {
    try {
      await usersApi.resetLogin(user.id);
      setNotice({ tone: "success", message: `Login flag reset for ${fullName(user)}.` });
      loadData();
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Unable to reset login flag." });
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Admin Visibility"
        title="User Activity"
        description="View online status, sign-in recency, account health, and report access scope in one place."
        compact
        actions={
          <button type="button" onClick={loadData} className="button-secondary" disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        }
      />

      <MetricStrip items={metricItems} />

      {summary ? (
        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
            <div className="label-caps">24h sign ins</div>
            <div className="mt-2 text-2xl font-semibold text-slate-950">{summary.signed_in_last_24h}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
            <div className="label-caps">30d sign ins</div>
            <div className="mt-2 text-2xl font-semibold text-slate-950">{summary.signed_in_last_30d}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
            <div className="label-caps">Password enabled</div>
            <div className="mt-2 text-2xl font-semibold text-slate-950">{summary.password_enabled_users}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
            <div className="label-caps">Never signed in</div>
            <div className="mt-2 text-2xl font-semibold text-slate-950">{summary.never_signed_in_users}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
            <div className="label-caps">Blocked / inactive</div>
            <div className="mt-2 text-2xl font-semibold text-slate-950">{summary.blocked_users} / {summary.inactive_users}</div>
          </div>
        </section>
      ) : null}

      {notice ? (
        <div
          className={`rounded-2xl border px-4 py-3 text-sm ${
            notice.tone === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-rose-200 bg-rose-50 text-rose-800"
          }`.trim()}
        >
          {notice.message}
        </div>
      ) : null}

      <SectionCard title="Activity Filters" subtitle="Online status is based on the app login flag; last login is the durable sign-in timestamp.">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr),auto] xl:items-center">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="input-shell w-full"
            placeholder="Search name, email, role, HMC, or report scope"
          />
          <SegmentedViewSwitch
            activeKey={view}
            onChange={(key) => setView(key as ViewKey)}
            items={[
              { key: "online", label: "Online", count: onlineUsers.length },
              { key: "recent", label: "Recent", count: recentUsers.length },
              { key: "all", label: "All", count: users.length },
              { key: "needs_attention", label: "Needs attention", count: attentionUsers.length },
            ]}
          />
        </div>
      </SectionCard>

      <DataTableShell title="360 User View" subtitle={`${filteredUsers.length} matching accounts`}>
        {filteredUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role / Scope</th>
                  <th>Login</th>
                  <th>Account</th>
                  <th>Contact</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const days = daysSince(user.last_login);
                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="font-semibold text-slate-900">{fullName(user)}</div>
                        <div className="mt-1 text-sm text-slate-600">{user.email}</div>
                      </td>
                      <td>
                        <div>{user.role?.name || "-"}</div>
                        <div className="mt-1 text-sm text-slate-600">{user.management_company_name || "No HMC affiliation"}</div>
                        <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">
                          {user.report_management_company_name
                            ? `${user.report_management_company_name} / ${user.report_view_mode}`
                            : "No report scope"}
                        </div>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-2">
                          <span className={`badge-pill ${user.is_logged_in ? "tone-success" : "tone-neutral"}`}>
                            {user.is_logged_in ? "Online" : "Offline"}
                          </span>
                          <span className={`badge-pill ${user.has_usable_password ? "tone-brand" : "tone-warning"}`}>
                            {user.has_usable_password ? "Password" : "No password"}
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-slate-600">Last: {formatDateTime(user.last_login)}</div>
                        <div className="mt-1 text-xs text-slate-500">{days == null ? "No sign-in record" : `${days} days ago`}</div>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-2">
                          <span className={`badge-pill ${user.is_active ? "tone-success" : "tone-danger"}`}>
                            {user.is_active ? "Active" : "Inactive"}
                          </span>
                          {user.is_blocked ? <span className="badge-pill tone-danger">Blocked</span> : null}
                          {user.is_staff || user.is_superuser ? <span className="badge-pill tone-neutral">Admin</span> : null}
                        </div>
                        <div className="mt-2 text-xs text-slate-500">Joined {formatDateTime(user.date_joined)}</div>
                        <div className="mt-1 text-xs text-slate-500">Updated {formatDateTime(user.updated_at)}</div>
                      </td>
                      <td>
                        <div>{user.business_phone || "-"}</div>
                        {user.personal_phone ? <div className="mt-1 text-sm text-slate-600">{user.personal_phone}</div> : null}
                      </td>
                      <td>
                        {user.is_logged_in ? (
                          <button type="button" onClick={() => resetLogin(user)} className="button-secondary !px-3 !py-2 text-xs">
                            Reset Login
                          </button>
                        ) : (
                          <span className="text-sm text-slate-500">No action</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No users found" message="No accounts match this activity view and search." />
        )}
      </DataTableShell>
    </div>
  );
}
